import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, GraduationCap, ArrowLeft, Loader } from 'lucide-react';

function PricingPage() {
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch('https://api.intuitlms.online/api/subscriptions/packages');
      const data = await response.json();
      setPackages(data);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold text-primary">INTUIT LMS</span>
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pay only for what you use. Our per-user pricing model ensures you get the best value
              as your institution grows. Payments are collected automatically through student fees.
            </p>
          </div>

          {/* Pricing Cards */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto">
              {packages.map((pkg) => (
                <div key={pkg.id} className="card flex flex-col h-full w-full max-w-md hover:shadow-lg transition-shadow duration-300">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.displayName}</h3>
                    <div className="flex items-baseline mb-4">
                      <span className="text-4xl font-bold text-primary">
                        {formatCurrency(pkg.pricePerStudentPerUnit)}
                      </span>
                      <span className="text-gray-600 ml-2">/student/unit</span>
                    </div>
                    <p className="text-gray-600">{pkg.description}</p>
                  </div>

                  <div className="flex-grow mb-8">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`/signup?package=${encodeURIComponent(pkg.name)}`}
                    className="btn-primary w-full text-center py-3 mt-auto"
                  >
                    Select Plan
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* How it Works */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How Payment Works</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="card text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Students Pay</h3>
                <p className="text-gray-600">
                  Students make payments for units through M-Pesa integration in the mobile app
                </p>
              </div>

              <div className="card text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Revenue Tracking</h3>
                <p className="text-gray-600">
                  System automatically tracks payments and calculates revenue per user
                </p>
              </div>

              <div className="card text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Access Control</h3>
                <p className="text-gray-600">
                  If payment thresholds aren't met, student and lecturer access is temporarily locked
                </p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="card">
                <h3 className="text-lg font-semibold mb-2">What happens if students don't pay?</h3>
                <p className="text-gray-600">
                  If the cumulative payments from your campus don't meet the minimum threshold,
                  access to the system will be temporarily suspended for students and lecturers until payments resume.
                </p>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-2">Is there a setup fee?</h3>
                <p className="text-gray-600">
                  No setup fees! We only charge based on actual usage through student payments.
                  Get started with our free trial to see how it works.
                </p>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600">
                  Yes, you can stop using the service at any time. Since payments are collected
                  through student fees, simply stop collecting those fees to discontinue service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join the growing number of institutions using INTUIT LMS to streamline their operations.
          </p>
          <Link to="/signup" className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Sign Up Now
          </Link>
        </div>
      </section>
    </div>
  );
}

export default PricingPage;
